import { useState, useEffect } from 'react';
import Modal from '../../BaseComponents/Modal';
import { RootState } from '../../../store/store';
import { useSelector, useDispatch } from 'react-redux';
import './MedicalCardModal.scss';
import Button from '../../BaseComponents/Button';
import { resendFormToMember, updateMemberMedicalCard, getMemberMedicalCard, updateMember } from '../../../api/members';
import TextInput from '../../BaseComponents/TextInput';
import TextArea from '../../BaseComponents/TextArea';
import Select from '../../BaseComponents/Select';
import MultiSelect from '../../BaseComponents/MultiSelect';
import PhoneInputComp from '../../BaseComponents/PhoneInput';
import { CustomInput } from '../../location/CustomInput';
import { races, getGenderDisplayLabel, genders, weightMeasurementTypes } from '../../../utils/constants';
import { convertWeight } from '../../../utils';
import {
  overallHealthOptions,
  activityLevelOptions,
  smokingStatusOptions,
  alcoholFrequencyOptions,
  dietTypeOptions,
  eatingPatternOptions,
  hydrationOptions,
  exerciseRoutineOptions,
  calmingActivityOptions,
  workingStyleOptions,
  medicalImplantOptions,
  practicalConsiderationOptions,
  normalizeSupplementsFromApi
} from '../../../types/membersTypes';
import { GoalsOption, HealthConcernOption } from '../../../types/apiGeneratedTypes';
import DatePickerComp from '../../BaseComponents/DatePicker';
import MedicalCardOnboardingSections from './MedicalCardOnboardingSections';
import MedicalCardYesNoDescribe from './MedicalCardYesNoDescribe';
import { setMemberMedicalCard, setMemberData } from '../../../store/memberSlice';

const goalsOptions = [
  { key: GoalsOption.IWantAComprehensiveHealthCheck, value: GoalsOption.IWantAComprehensiveHealthCheck },
  { key: GoalsOption.ImFeelingUnwellAndWantClarity, value: GoalsOption.ImFeelingUnwellAndWantClarity },
  { key: GoalsOption.IWantToTakeControlOfMyHealth, value: GoalsOption.IWantToTakeControlOfMyHealth },
  { key: GoalsOption.ImInterestedInPreventativeHealthAndLongevity, value: GoalsOption.ImInterestedInPreventativeHealthAndLongevity },
  { key: GoalsOption.MyDoctorCoachRecommendedIt, value: GoalsOption.MyDoctorCoachRecommendedIt },
  { key: GoalsOption.Other, value: GoalsOption.Other }
];

const healthConcernsOptions = [
  { key: HealthConcernOption.FamilyHistoryGeneticRisk, value: HealthConcernOption.FamilyHistoryGeneticRisk },
  { key: HealthConcernOption.HormonalHealth, value: HealthConcernOption.HormonalHealth },
  { key: HealthConcernOption.FertilityPlanning, value: HealthConcernOption.FertilityPlanning },
  { key: HealthConcernOption.DealingWithAnExistingCondition, value: HealthConcernOption.DealingWithAnExistingCondition },
  { key: HealthConcernOption.InterestedInLongevityBiohacking, value: HealthConcernOption.InterestedInLongevityBiohacking },
  { key: HealthConcernOption.MetabolicHealth, value: HealthConcernOption.MetabolicHealth },
  { key: HealthConcernOption.Other, value: HealthConcernOption.Other },
  { key: HealthConcernOption.None, value: HealthConcernOption.None }
];

const HEALTH_CONCERN_NONE_KEY = HealthConcernOption.None;
const MRI_NONE_KEY = 'None of the above';

type YesNoAnswer = 'yes' | 'no';

/** Empty string = no; non-empty = yes + detail; null/undefined = unanswered. */
const yesNoFromStoredDetail = (
  stored: unknown
): { answer: YesNoAnswer | undefined; detail: string } => {
  if (stored === undefined || stored === null) {
    return { answer: undefined, detail: '' };
  }
  const text = String(stored);
  if (text.trim() === '') {
    return { answer: 'no', detail: '' };
  }
  return { answer: 'yes', detail: text };
};

/** Onboarding may persist fields beyond the generated AddressModel. */
type MedicalCardAddress = {
  full_address?: string | null;
  postcode?: string | null;
  city?: string | null;
  country?: string | null;
  address_line_1?: string | null;
};

const getMedicalCardAddress = (address: unknown): MedicalCardAddress =>
  (address && typeof address === 'object' ? address : {}) as MedicalCardAddress;

const hormonalContraceptionOptions = [
  { key: 'No', value: 'No' },
  { key: 'Yes - Combined pill', value: 'Yes - Combined pill' },
  { key: 'Yes - Progestin-only pill', value: 'Yes - Progestin-only pill' },
  { key: 'Yes - IUD (hormonal)', value: 'Yes - IUD (hormonal)' },
  { key: 'Yes - Implant', value: 'Yes - Implant' },
  { key: 'Yes - Patch', value: 'Yes - Patch' },
  { key: 'Yes - Ring', value: 'Yes - Ring' },
  { key: 'Yes - HRT', value: 'Yes - HRT' },
  { key: 'Yes - Other', value: 'Yes - Other' }
];

const cycleLengthOptions = [
  { key: '21 days', value: '21 days' },
  { key: '22 days', value: '22 days' },
  { key: '23 days', value: '23 days' },
  { key: '24 days', value: '24 days' },
  { key: '25 days', value: '25 days' },
  { key: '26 days', value: '26 days' },
  { key: '27 days', value: '27 days' },
  { key: '28 days', value: '28 days' },
  { key: '29 days', value: '29 days' },
  { key: '30 days', value: '30 days' },
  { key: '31 days', value: '31 days' },
  { key: '32 days', value: '32 days' },
  { key: '33 days', value: '33 days' },
  { key: '34 days', value: '34 days' },
  { key: '35 days', value: '35 days' },
  { key: 'Irregular', value: 'Irregular' },
  { key: 'Not sure', value: 'Not sure' }
];

const primaryTestingReasonOptions = [
  { key: 'PMS symptoms', value: 'PMS symptoms' },
  { key: 'Fertility concerns', value: 'Fertility concerns' },
  { key: 'Cycle mapping', value: 'Cycle mapping' },
  { key: 'Fatigue', value: 'Fatigue' },
  { key: 'Irregular periods', value: 'Irregular periods' },
  { key: 'Heavy periods', value: 'Heavy periods' },
  { key: 'Painful periods', value: 'Painful periods' },
  { key: 'Hormonal imbalance concerns', value: 'Hormonal imbalance concerns' },
  { key: 'Menopause symptoms', value: 'Menopause symptoms' },
  { key: 'Other', value: 'Other' }
];

const menstrualFlowOptions = [
  { key: 'Heavy', value: 'Heavy' },
  { key: 'Moderate', value: 'Moderate' },
  { key: 'Light', value: 'Light' },
  { key: 'Very light', value: 'Very light' },
  { key: 'Painful', value: 'Painful' },
  { key: 'Spotting', value: 'Spotting' },
  { key: 'Irregular', value: 'Irregular' },
  { key: 'Other', value: 'Other' }
];

type Option = { key: string; value: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MedicalCardModal = ({ isOpen, onClose }: Props) => {
  const dispatch = useDispatch();
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const { admin: { role } } = useSelector((state: RootState) => state.admin);
  const { medicalCard, memberData } = useSelector(
    (state: RootState) => state.member
  );
  const visible = ((role !== "doctor") || (assigned_doctor && role === "doctor"));
  
  const [isResendingForm, setIsResendingForm] = useState(false);
  const [isResendError, setIsResendError] = useState(false);
  const [isGeneralInfoEditOpen, setIsGeneralInfoEditOpen] = useState(false);
  const [isGoalsEditOpen, setIsGoalsEditOpen] = useState(false);
  const [isHealthLifestyleEditOpen, setIsHealthLifestyleEditOpen] = useState(false);
  const [isMedicalHistoryEditOpen, setIsMedicalHistoryEditOpen] = useState(false);
  const [isFemaleHormonesEditOpen, setIsFemaleHormonesEditOpen] = useState(false);
  const [isScanInfoEditOpen, setIsScanInfoEditOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Personal Information
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [personalInfoErrors, setPersonalInfoErrors] = useState<Record<string, string>>({});
  const [selectedGender, setSelectedGender] = useState<Option | null>(null);
  const [selectedRaceEthnicity, setSelectedRaceEthnicity] = useState<Option | null>(null);
  const [addressValue, setAddressValue] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [cityValue, setCityValue] = useState('');
  const [postcodeValue, setPostcodeValue] = useState('');
  const [countryValue, setCountryValue] = useState('');
  const [weightValue, setWeightValue] = useState('');
  const [heightValue, setHeightValue] = useState('');
  const [selectedWeightUnit, setSelectedWeightUnit] = useState<Option | null>(null);
  const [selectedBirthDate, setSelectedBirthDate] = useState<Date | null>(null);

  // Current Lifestyle
  const [lifestyleErrors, setLifestyleErrors] = useState<Record<string, string>>({});
  const [selectedOverallHealth, setSelectedOverallHealth] = useState<Option | null>(null);
  const [selectedActivityLevel, setSelectedActivityLevel] = useState<Option | null>(null);
  const [selectedAlcoholFrequency, setSelectedAlcoholFrequency] = useState<Option | null>(null);
  const [selectedSmokingStatus, setSelectedSmokingStatus] = useState<Option | null>(null);
  const [selectedCalmingActivities, setSelectedCalmingActivities] = useState<Option[]>([]);
  const [selectedDietTypes, setSelectedDietTypes] = useState<Option[]>([]);
  const [selectedEatingPattern, setSelectedEatingPattern] = useState<Option | null>(null);
  const [selectedExerciseRoutine, setSelectedExerciseRoutine] = useState<Option | null>(null);
  const [selectedHydration, setSelectedHydration] = useState<Option | null>(null);
  const [selectedWorkingStyle, setSelectedWorkingStyle] = useState<Option | null>(null);
  const [otherExerciseRoutine, setOtherExerciseRoutine] = useState('');
  const [otherWorkingStyle, setOtherWorkingStyle] = useState('');
  const [otherDietType, setOtherDietType] = useState('');
  const [otherEatingPattern, setOtherEatingPattern] = useState('');
  const [otherHydration, setOtherHydration] = useState('');
  const [otherCalmingActivity, setOtherCalmingActivity] = useState('');

  // Medical History
  const [medicalHistoryErrors, setMedicalHistoryErrors] = useState<Record<string, string>>({});
  const [hasMedicalConditions, setHasMedicalConditions] = useState<YesNoAnswer | undefined>();
  const [medicalConditionsDetail, setMedicalConditionsDetail] = useState('');
  const [hasSurgeries, setHasSurgeries] = useState<YesNoAnswer | undefined>();
  const [surgeriesDetail, setSurgeriesDetail] = useState('');
  const [hasMedications, setHasMedications] = useState<YesNoAnswer | undefined>();
  const [medicationsDetail, setMedicationsDetail] = useState('');
  const [hasAllergies, setHasAllergies] = useState<YesNoAnswer | undefined>();
  const [allergiesDetail, setAllergiesDetail] = useState('');

  // Goals
  const [goalsErrors, setGoalsErrors] = useState<Record<string, string>>({});
  const [selectedGoals, setSelectedGoals] = useState<Option[]>([]);
  const [selectedHealthConcerns, setSelectedHealthConcerns] = useState<Option[]>([]);
  const [healthConcernDetails, setHealthConcernDetails] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [selectedLastBloodTest, setSelectedLastBloodTest] = useState<Date | null>(null);

  // MRI Scan Safety
  const [scanErrors, setScanErrors] = useState<Record<string, string>>({});
  const [reasonForScan, setReasonForScan] = useState('');
  const [currentSymptoms, setCurrentSymptoms] = useState('');
  const [cancerAnswer, setCancerAnswer] = useState<YesNoAnswer | undefined>();
  const [cancerHistoryDetails, setCancerHistoryDetails] = useState('');
  const [previousImaging, setPreviousImaging] = useState('');
  const [scanFamilyHistory, setScanFamilyHistory] = useState('');
  const [selectedMedicalImplants, setSelectedMedicalImplants] = useState<Option[]>([]);
  const [implantDetails, setImplantDetails] = useState('');
  const [selectedPracticalConsiderations, setSelectedPracticalConsiderations] = useState<Option[]>([]);
  const [practicalDetails, setPracticalDetails] = useState('');

  // Female Sex Hormones
  const [selectedLastPeriodDate, setSelectedLastPeriodDate] = useState<Date | null>(null);
  const [selectedHormonalContraception, setSelectedHormonalContraception] = useState<Option | null>(null);
  const [hormonalContraceptionOther, setHormonalContraceptionOther] = useState('');
  const [selectedCycleLength, setSelectedCycleLength] = useState<Option | null>(null);
  const [selectedPrimaryTestingReason, setSelectedPrimaryTestingReason] = useState<Option | null>(null);
  const [primaryTestingReasonOther, setPrimaryTestingReasonOther] = useState('');
  const [selectedMenstrualFlow, setSelectedMenstrualFlow] = useState<Option | null>(null);
  const [menstrualFlowOther, setMenstrualFlowOther] = useState('');
  const [menopauseDetails, setMenopauseDetails] = useState('');
  const [additionalNotesForClinicalTeam, setAdditionalNotesForClinicalTeam] = useState('');

  const clearFieldError = (
    setter: (updater: (prev: Record<string, string>) => Record<string, string>) => void,
    field: string
  ) => {
    setter((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  useEffect(() => {
    if (!isGeneralInfoEditOpen) return;
    const basics = medicalCard?.basics as Record<string, any> | undefined;
    const addr = getMedicalCardAddress(basics?.address);
    setFirstName(basics?.first_name || memberData?.first_name || '');
    setLastName(basics?.last_name || memberData?.last_name || '');
    setAddressValue(addr.full_address || '');
    setAddressLine1(addr.address_line_1 || '');
    setCityValue(addr.city || '');
    setPostcodeValue(addr.postcode || '');
    setCountryValue(addr.country || '');
    setSelectedBirthDate(
      basics?.birth_date ? new Date(basics.birth_date) : null
    );
    setSelectedGender(
      basics?.biological_sex
        ? {
            key: basics.biological_sex,
            value: getGenderDisplayLabel(basics.biological_sex)
          }
        : null
    );
    setSelectedRaceEthnicity(
      basics?.race_ethnicity
        ? races.find((r) => r.key === basics.race_ethnicity) || {
            key: basics.race_ethnicity,
            value: basics.race_ethnicity
          }
        : null
    );
    setWeightValue(basics?.weight != null ? String(basics.weight) : '');
    setHeightValue(basics?.height != null ? String(basics.height) : '');
    const weightUnit = basics?.weight_unit;
    setSelectedWeightUnit(
      weightUnit
        ? weightMeasurementTypes.find((u) => u.key === weightUnit) || {
            key: weightUnit,
            value: weightUnit
          }
        : weightMeasurementTypes[0]
    );
    setPersonalInfoErrors({});
  }, [isGeneralInfoEditOpen, medicalCard?.basics, memberData]);

  useEffect(() => {
    if (!isHealthLifestyleEditOpen) return;
    const health = medicalCard?.health_lifestyle;
    const nutritionist = medicalCard?.nutritionist_questions;

    const overallHealth = health?.overall_health;
    setSelectedOverallHealth(
      overallHealth
        ? overallHealthOptions.find((opt) => opt.key === overallHealth) || {
            key: overallHealth,
            value: overallHealth
          }
        : null
    );

    const activityLevel = health?.activity_level;
    setSelectedActivityLevel(
      activityLevel
        ? activityLevelOptions.find((opt) => opt.key === activityLevel) || {
            key: activityLevel,
            value: activityLevel
          }
        : null
    );

    const alcoholFrequency = health?.alcohol_frequency;
    setSelectedAlcoholFrequency(
      alcoholFrequency
        ? alcoholFrequencyOptions.find((opt) => opt.key === alcoholFrequency) || {
            key: alcoholFrequency,
            value: alcoholFrequency
          }
        : null
    );

    const smokingStatus = health?.smoking_status;
    setSelectedSmokingStatus(
      smokingStatus
        ? smokingStatusOptions.find((opt) => opt.key === smokingStatus) || {
            key: smokingStatus,
            value: smokingStatus
          }
        : null
    );

    if (nutritionist?.calming_activities && Array.isArray(nutritionist.calming_activities)) {
      setSelectedCalmingActivities(
        nutritionist.calming_activities
          .map((activity: string) => {
            const found = calmingActivityOptions.find(
              (opt) => opt.key === activity || opt.value === activity
            );
            return found || { key: activity, value: activity };
          })
          .filter((item: Option) => item.key)
      );
    } else {
      setSelectedCalmingActivities([]);
    }

    if (nutritionist?.diet_types && Array.isArray(nutritionist.diet_types)) {
      setSelectedDietTypes(
        nutritionist.diet_types
          .map((diet: string) => {
            const found = dietTypeOptions.find(
              (opt) => opt.key === diet || opt.value === diet
            );
            return found || { key: diet, value: diet };
          })
          .filter((item: Option) => item.key)
      );
    } else {
      setSelectedDietTypes([]);
    }

    const eatingPattern = nutritionist?.eating_pattern;
    setSelectedEatingPattern(
      eatingPattern
        ? eatingPatternOptions.find(
            (opt) => opt.key === eatingPattern || opt.value === eatingPattern
          ) || { key: eatingPattern, value: eatingPattern }
        : null
    );

    const exerciseRoutine = nutritionist?.exercise_routine;
    setSelectedExerciseRoutine(
      exerciseRoutine
        ? exerciseRoutineOptions.find(
            (opt) => opt.key === exerciseRoutine || opt.value === exerciseRoutine
          ) || { key: exerciseRoutine, value: exerciseRoutine }
        : null
    );

    const hydration = nutritionist?.hydration;
    setSelectedHydration(
      hydration
        ? hydrationOptions.find(
            (opt) => opt.key === hydration || opt.value === hydration
          ) || { key: hydration, value: hydration }
        : null
    );

    const workingStyle = nutritionist?.working_style;
    setSelectedWorkingStyle(
      workingStyle
        ? workingStyleOptions.find(
            (opt) => opt.key === workingStyle || opt.value === workingStyle
          ) || { key: workingStyle, value: workingStyle }
        : null
    );

    setOtherExerciseRoutine(nutritionist?.other_exercise_routine || '');
    setOtherWorkingStyle(nutritionist?.other_working_style || '');
    setOtherDietType(nutritionist?.other_diet_type || '');
    setOtherEatingPattern(nutritionist?.other_eating_pattern || '');
    setOtherHydration(nutritionist?.other_hydration || '');
    setOtherCalmingActivity(nutritionist?.other_calming_activity || '');
    setLifestyleErrors({});
  }, [
    isHealthLifestyleEditOpen,
    medicalCard?.health_lifestyle,
    medicalCard?.nutritionist_questions
  ]);

  useEffect(() => {
    if (!isMedicalHistoryEditOpen || !medicalCard?.health_lifestyle) return;
    const health = medicalCard.health_lifestyle as Record<string, any>;

    const conditions = yesNoFromStoredDetail(health.medical_conditions);
    setHasMedicalConditions(conditions.answer);
    setMedicalConditionsDetail(conditions.detail);

    const surgeries = yesNoFromStoredDetail(health.previous_surgeries);
    setHasSurgeries(surgeries.answer);
    setSurgeriesDetail(surgeries.detail);

    const meds = yesNoFromStoredDetail(
      normalizeSupplementsFromApi(health.supplements) || health.medications
    );
    setHasMedications(meds.answer);
    setMedicationsDetail(meds.detail);

    const allergies = yesNoFromStoredDetail(health.allergies);
    setHasAllergies(allergies.answer);
    setAllergiesDetail(allergies.detail);

    setMedicalHistoryErrors({});
  }, [isMedicalHistoryEditOpen, medicalCard?.health_lifestyle]);

  useEffect(() => {
    if (!isGoalsEditOpen || !medicalCard?.goals) return;
    const goals = medicalCard.goals as Record<string, any>;

    if (goals.goals && Array.isArray(goals.goals)) {
      setSelectedGoals(
        goals.goals
          .map((goal: string) => {
            const found = goalsOptions.find(
              (opt) => opt.key === goal || opt.value === goal
            );
            return found || { key: goal, value: goal };
          })
          .filter((item: Option) => item.key)
      );
    } else {
      setSelectedGoals([]);
    }

    if (goals.health_concerns && Array.isArray(goals.health_concerns)) {
      setSelectedHealthConcerns(
        goals.health_concerns
          .map((concern: string) => {
            const found = healthConcernsOptions.find(
              (opt) => opt.key === concern || opt.value === concern
            );
            return found || { key: concern, value: concern };
          })
          .filter((item: Option) => item.key)
      );
    } else {
      setSelectedHealthConcerns([]);
    }

    setHealthConcernDetails(goals.health_concern_details || '');
    setAdditionalInfo(goals.additional_info || '');
    setSelectedLastBloodTest(
      goals.last_blood_test ? new Date(goals.last_blood_test) : null
    );
    setGoalsErrors({});
  }, [isGoalsEditOpen, medicalCard?.goals]);

  useEffect(() => {
    if (!isScanInfoEditOpen) return;
    const scanInfo = (medicalCard?.scan_info || {}) as Record<string, any>;

    setReasonForScan(scanInfo.reason_for_scan || '');
    setCurrentSymptoms(scanInfo.current_symptoms || '');
    setPreviousImaging(scanInfo.any_previous_imaging || '');
    setScanFamilyHistory(scanInfo.scan_family_history_details || '');
    setImplantDetails(scanInfo.metallic_implants_details || '');
    setPracticalDetails(scanInfo.practical_considerations_details || '');

    const cancerHistory = scanInfo.cancer_history;
    if (cancerHistory === 'Yes' || cancerHistory === 'yes') {
      setCancerAnswer('yes');
      setCancerHistoryDetails(scanInfo.cancer_history_details || '');
    } else if (cancerHistory === 'No' || cancerHistory === 'no') {
      setCancerAnswer('no');
      setCancerHistoryDetails('');
    } else {
      setCancerAnswer(undefined);
      setCancerHistoryDetails(scanInfo.cancer_history_details || '');
    }

    if (Array.isArray(scanInfo.medical_implants)) {
      setSelectedMedicalImplants(
        scanInfo.medical_implants.map((item: string) => {
          const found = medicalImplantOptions.find(
            (opt) => opt.key === item || opt.value === item
          );
          return found || { key: item, value: item };
        })
      );
    } else {
      setSelectedMedicalImplants([]);
    }

    if (Array.isArray(scanInfo.practical_considerations)) {
      setSelectedPracticalConsiderations(
        scanInfo.practical_considerations.map((item: string) => {
          const found = practicalConsiderationOptions.find(
            (opt) => opt.key === item || opt.value === item
          );
          return found || { key: item, value: item };
        })
      );
    } else {
      setSelectedPracticalConsiderations([]);
    }

    setScanErrors({});
  }, [isScanInfoEditOpen, medicalCard?.scan_info]);

  useEffect(() => {
    if (!isFemaleHormonesEditOpen || !medicalCard?.female_sex_hormones) return;
    const hormones = medicalCard.female_sex_hormones;

    setSelectedLastPeriodDate(
      hormones.last_period_date ? new Date(hormones.last_period_date) : null
    );

    const hormonalContraception = hormones.hormonal_contraception_hrt;
    if (hormonalContraception) {
      const found = hormonalContraceptionOptions.find(
        (opt) =>
          opt.key === hormonalContraception || opt.value === hormonalContraception
      );
      if (found) {
        setSelectedHormonalContraception(found);
        setHormonalContraceptionOther('');
      } else {
        setSelectedHormonalContraception({
          key: 'Yes - Other',
          value: 'Yes - Other'
        });
        setHormonalContraceptionOther(hormonalContraception);
      }
    } else {
      setSelectedHormonalContraception(null);
      setHormonalContraceptionOther('');
    }

    const cycleLength = hormones.typical_cycle_length;
    setSelectedCycleLength(
      cycleLength
        ? cycleLengthOptions.find(
            (opt) => opt.key === cycleLength || opt.value === cycleLength
          ) || { key: cycleLength, value: cycleLength }
        : null
    );

    const primaryReason = hormones.primary_testing_reason;
    if (primaryReason) {
      const found = primaryTestingReasonOptions.find(
        (opt) => opt.key === primaryReason || opt.value === primaryReason
      );
      if (found) {
        setSelectedPrimaryTestingReason(found);
        setPrimaryTestingReasonOther('');
      } else {
        setSelectedPrimaryTestingReason({ key: 'Other', value: 'Other' });
        setPrimaryTestingReasonOther(primaryReason);
      }
    } else {
      setSelectedPrimaryTestingReason(null);
      setPrimaryTestingReasonOther('');
    }

    const menstrualFlow = hormones.menstrual_flow_characteristics;
    if (menstrualFlow) {
      const found = menstrualFlowOptions.find(
        (opt) => opt.key === menstrualFlow || opt.value === menstrualFlow
      );
      if (found) {
        setSelectedMenstrualFlow(found);
        setMenstrualFlowOther('');
      } else {
        setSelectedMenstrualFlow({ key: 'Other', value: 'Other' });
        setMenstrualFlowOther(menstrualFlow);
      }
    } else {
      setSelectedMenstrualFlow(null);
      setMenstrualFlowOther('');
    }

    setMenopauseDetails(hormones.menopause_details || '');
    setAdditionalNotesForClinicalTeam(
      hormones.additional_notes_for_clinical_team || ''
    );
  }, [isFemaleHormonesEditOpen, medicalCard?.female_sex_hormones]);

  const handleCalmingActivitiesChange = (option: Option, _fieldName: string) => {
    const isSelected = selectedCalmingActivities.some((act) => act.key === option.key);
    const updated = isSelected
      ? selectedCalmingActivities.filter((act) => act.key !== option.key)
      : [...selectedCalmingActivities, option];
    setSelectedCalmingActivities(updated);
    clearFieldError(setLifestyleErrors, 'calming_activities');
    if (!updated.some((act) => act.key === 'Other')) {
      setOtherCalmingActivity('');
      clearFieldError(setLifestyleErrors, 'other_calming_activity');
    }
  };

  const handleDietTypesChange = (option: Option, _fieldName: string) => {
    const isSelected = selectedDietTypes.some((diet) => diet.key === option.key);
    const updated = isSelected
      ? selectedDietTypes.filter((diet) => diet.key !== option.key)
      : [...selectedDietTypes, option];
    setSelectedDietTypes(updated);
    clearFieldError(setLifestyleErrors, 'diet_types');
    if (!updated.some((diet) => diet.key === 'Other')) {
      setOtherDietType('');
      clearFieldError(setLifestyleErrors, 'other_diet_type');
    }
  };

  const handleGoalsChange = (option: Option, _fieldName: string) => {
    const isSelected = selectedGoals.some((goal) => goal.key === option.key);
    setSelectedGoals(
      isSelected
        ? selectedGoals.filter((goal) => goal.key !== option.key)
        : [...selectedGoals, option]
    );
  };

  const handleHealthConcernsChange = (option: Option, _fieldName: string) => {
    const isSelected = selectedHealthConcerns.some(
      (concern) => concern.key === option.key
    );
    let updated: Option[];
    if (option.key === HEALTH_CONCERN_NONE_KEY) {
      updated = isSelected ? [] : [option];
    } else {
      const withoutNone = selectedHealthConcerns.filter(
        (item) => item.key !== HEALTH_CONCERN_NONE_KEY
      );
      updated = isSelected
        ? withoutNone.filter((item) => item.key !== option.key)
        : [...withoutNone, option];
    }
    setSelectedHealthConcerns(updated);
    clearFieldError(setGoalsErrors, 'health_concerns');
    if (!updated.length || updated.every((item) => item.key === HEALTH_CONCERN_NONE_KEY)) {
      setHealthConcernDetails('');
      clearFieldError(setGoalsErrors, 'health_concern_details');
    }
  };

  const handleExclusiveMultiSelect = (
    option: Option,
    current: Option[],
    setCurrent: (next: Option[]) => void,
    detailsSetter: (value: string) => void,
    fieldErrorKey: string,
    detailsErrorKey: string
  ) => {
    const isSelected = current.some((item) => item.key === option.key);
    let updated: Option[];
    if (option.key === MRI_NONE_KEY) {
      updated = isSelected ? [] : [option];
    } else {
      const withoutNone = current.filter((item) => item.key !== MRI_NONE_KEY);
      updated = isSelected
        ? withoutNone.filter((item) => item.key !== option.key)
        : [...withoutNone, option];
    }
    setCurrent(updated);
    clearFieldError(setScanErrors, fieldErrorKey);
    if (!updated.length || updated.every((item) => item.key === MRI_NONE_KEY)) {
      detailsSetter('');
      clearFieldError(setScanErrors, detailsErrorKey);
    }
  };

  const handleWeightUnitChange = (option: Option) => {
    const numericValue = parseFloat(weightValue);
    if (
      !isNaN(numericValue) &&
      selectedWeightUnit?.key &&
      selectedWeightUnit.key !== option.key
    ) {
      const converted = convertWeight(numericValue, selectedWeightUnit.key, option.key);
      setWeightValue(converted ? converted.toFixed(2) : '');
    }
    setSelectedWeightUnit(option);
  };

  if (!medicalCard || !memberData?.user_id) return null;

  const address = getMedicalCardAddress(medicalCard.basics?.address);
  const basicsAny = medicalCard.basics as Record<string, any> | undefined;
  const emailDisplay = basicsAny?.email || memberData.email || '';
  const phoneDisplay = basicsAny?.phone || memberData.phone || '';

  const hasConcernDetailsRequired = selectedHealthConcerns.some(
    (item) => item.key !== HEALTH_CONCERN_NONE_KEY
  );
  const hasImplantSelectionRequiringDetails = selectedMedicalImplants.some(
    (item) => item.key !== MRI_NONE_KEY
  );
  const hasPracticalSelectionRequiringDetails = selectedPracticalConsiderations.some(
    (item) => item.key !== MRI_NONE_KEY
  );

  const refreshMedicalCard = async () => {
    try {
      const updatedCard = await getMemberMedicalCard(memberData.user_id);
      dispatch(setMemberMedicalCard(updatedCard));
      return updatedCard;
    } catch (error) {
      console.error('Error refreshing medical card:', error);
      throw error;
    }
  };

  const syncMemberProfileFromBasics = async (basics: Record<string, any>) => {
    if (!memberData?.user_id) return;

    const nextMemberData = {
      ...memberData,
      ...(basics.first_name != null ? { first_name: basics.first_name } : {}),
      ...(basics.last_name != null ? { last_name: basics.last_name } : {}),
      ...(basics.phone != null ? { phone: basics.phone } : {}),
      ...(basics.email != null ? { email: basics.email } : {}),
      ...(basics.biological_sex != null ? { gender: basics.biological_sex } : {}),
      ...(basics.birth_date != null ? { dob: basics.birth_date } : {})
    };
    dispatch(setMemberData(nextMemberData));

    try {
      await updateMember({
        user_id: memberData.user_id,
        first_name: basics.first_name,
        last_name: basics.last_name,
        phone: basics.phone,
        gender: basics.biological_sex,
        dob: basics.birth_date
      });
    } catch (error) {
      console.error('Error syncing member profile from medical card:', error);
    }
  };

  const handleUpdateMedicalCard = async (updatedFields: any) => {
    if (!memberData?.user_id) return false;
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const mergedMedicalCard = {
        ...medicalCard,
        ...updatedFields
      };
      const updatePayload = {
        member_id: memberData.user_id,
        medical_card: mergedMedicalCard
      };
      
      const response = await updateMemberMedicalCard(updatePayload);
      
      if ('detail' in response) {
        const errorDetail = Array.isArray(response.detail)
          ? response.detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ')
          : JSON.stringify(response.detail);
        setSaveError(errorDetail || 'Validation error occurred');
        return false;
      }
      
      if ('status' in response && response.status !== 'success' && response.status !== 'updated') {
        setSaveError('Failed to update medical card');
        return false;
      }

      // Update UI immediately; refresh below reconciles with server.
      dispatch(setMemberMedicalCard(mergedMedicalCard));
      if (updatedFields?.basics) {
        await syncMemberProfileFromBasics(updatedFields.basics);
      }

      try {
        await refreshMedicalCard();
      } catch {
        // Keep optimistic update if refresh fails.
      }
      return true;
    } catch (error: any) {
      console.error('Error updating medical card:', error);
      setSaveError(error?.response?.data?.detail || error?.message || 'An error occurred while updating');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const validatePersonalInfo = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.first_name = 'First name is required';
    else if (/[^a-zA-Z\s]/.test(firstName)) {
      errors.first_name = 'First name can only contain letters';
    }
    if (!lastName.trim()) errors.last_name = 'Last name is required';
    else if (/[^a-zA-Z\s]/.test(lastName)) {
      errors.last_name = 'Last name can only contain letters';
    }
    if (!selectedBirthDate) errors.birthDate = 'Date of Birth is required';
    if (!selectedGender?.value) errors.gender = 'Sex assigned at birth is required';
    if (!weightValue.trim()) errors.weight = 'Weight is required';
    else if (/[a-zA-Z]/.test(weightValue)) errors.weight = 'Weight must contain only numbers';
    if (!heightValue.trim()) errors.height = 'Height is required';
    else if (/[a-zA-Z]/.test(heightValue)) errors.height = 'Height must contain only numbers';
    if (!selectedRaceEthnicity?.value) errors.race = 'Race/ethnicity is required';
    if (!emailDisplay.trim()) errors.email = 'Email is required';
    if (!phoneDisplay.trim()) errors.phone = 'Mobile phone number is required';
    if (!addressValue.trim()) errors.full_address = 'Address is required';
    if (!addressLine1.trim()) {
      errors.address_line_1 = 'Please select a complete address from the suggestions';
    }
    if (!cityValue.trim()) errors.city = 'City is required';
    if (!postcodeValue.trim()) errors.postalCode = 'Postal code is required';
    if (!countryValue.trim()) errors.country = 'Country is required';
    return errors;
  };

  const validateLifestyle = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!selectedOverallHealth?.value) errors.overall_health = 'Overall health is required';
    if (!selectedActivityLevel?.value) errors.activity_level = 'Activity level is required';
    if (!selectedExerciseRoutine?.value) {
      errors.exercise_routine = 'Exercise routine is required';
    } else if (
      selectedExerciseRoutine.key === 'Other' &&
      !otherExerciseRoutine.trim()
    ) {
      errors.other_exercise_routine = 'Please specify your exercise routine';
    }
    if (!selectedSmokingStatus?.value) errors.smoking_status = 'Smoking status is required';
    if (!selectedAlcoholFrequency?.value) {
      errors.alcohol_frequency = 'Alcohol frequency is required';
    }
    if (!selectedWorkingStyle?.value) {
      errors.working_style = 'Working style is required';
    } else if (selectedWorkingStyle.key === 'Other' && !otherWorkingStyle.trim()) {
      errors.other_working_style = 'Please specify your working style';
    }
    if (!selectedDietTypes.length) {
      errors.diet_types = 'Please select at least one diet type';
    } else if (
      selectedDietTypes.some((d) => d.key === 'Other') &&
      !otherDietType.trim()
    ) {
      errors.other_diet_type = 'Please specify your diet type';
    }
    if (!selectedEatingPattern?.value) {
      errors.eating_pattern = 'Eating pattern is required';
    } else if (
      selectedEatingPattern.key === 'Other' &&
      !otherEatingPattern.trim()
    ) {
      errors.other_eating_pattern = 'Please specify your eating pattern';
    }
    if (!selectedHydration?.value) {
      errors.hydration = 'Hydration method is required';
    } else if (selectedHydration.key === 'Other' && !otherHydration.trim()) {
      errors.other_hydration = 'Please specify your hydration method';
    }
    if (!selectedCalmingActivities.length) {
      errors.calming_activities = 'Please select at least one activity';
    } else if (
      selectedCalmingActivities.some((a) => a.key === 'Other') &&
      !otherCalmingActivity.trim()
    ) {
      errors.other_calming_activity = 'Please specify your calming activity';
    }
    return errors;
  };

  const validateMedicalHistory = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    const requireAnswer = (
      answer: YesNoAnswer | undefined,
      detail: string,
      answerKey: string,
      detailKey: string
    ) => {
      if (answer !== 'yes' && answer !== 'no') {
        errors[answerKey] = 'Please select yes or no';
        return;
      }
      if (answer === 'yes' && !detail.trim()) {
        errors[detailKey] = 'Please provide more details';
      }
    };
    requireAnswer(
      hasMedicalConditions,
      medicalConditionsDetail,
      'has_medical_conditions',
      'medical_conditions'
    );
    requireAnswer(hasSurgeries, surgeriesDetail, 'has_surgeries', 'previous_surgeries');
    requireAnswer(
      hasMedications,
      medicationsDetail,
      'has_medications_supplements',
      'supplements'
    );
    requireAnswer(hasAllergies, allergiesDetail, 'has_allergies', 'allergies');
    return errors;
  };

  const validateGoals = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!selectedHealthConcerns.length) {
      errors.health_concerns = 'Please select at least one option';
    } else if (hasConcernDetailsRequired && !healthConcernDetails.trim()) {
      errors.health_concern_details = 'Please provide more details';
    }
    return errors;
  };

  const validateScanInfo = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!reasonForScan.trim()) errors.reason_for_scan = 'This field is required';
    if (!currentSymptoms.trim()) errors.current_symptoms = 'This field is required';
    if (cancerAnswer !== 'yes' && cancerAnswer !== 'no') {
      errors.has_cancer_history = 'Please select yes or no';
    } else if (cancerAnswer === 'yes' && !cancerHistoryDetails.trim()) {
      errors.cancer_history_details = 'Please provide more details';
    }
    if (!selectedMedicalImplants.length) {
      errors.medical_implants = 'Please select at least one option';
    } else if (hasImplantSelectionRequiringDetails && !implantDetails.trim()) {
      errors.medical_implants_details = 'Please provide more details';
    }
    if (!selectedPracticalConsiderations.length) {
      errors.practical_considerations = 'Please select at least one option';
    } else if (hasPracticalSelectionRequiringDetails && !practicalDetails.trim()) {
      errors.practical_considerations_details = 'Please provide more details';
    }
    return errors;
  };

  const onResendFormClick = async () => {
    if (isResendError) setIsResendError(false);
    try {
      setIsResendingForm(true);
      await resendFormToMember(memberData?.user_id!);
    } catch (error) {
      console.log('error :>> ', error);
      setIsResendError(true);
    } finally {
      setIsResendingForm(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title='Member Medical Information'
      onClose={() => {
        onClose();
      }}
      width={'800px'}
    >
      <div>
        {!medicalCard.created_at && (
          <div className='resend-btn-wrapper'>
            {visible && 
            <Button
              text='Resend onboarding form'
              onClick={onResendFormClick}
              isLoading={isResendingForm}
            />}
            {isResendError && (
              <div className='error-msg'>
                Error happened, please reload window and try again.
              </div>
            )}
          </div>
        )}
        <MedicalCardOnboardingSections
          medicalCard={medicalCard}
          memberData={memberData}
          visible={visible}
          onEditSection={(title) => {
            switch (title) {
              case 'Personal Information':
                setIsGeneralInfoEditOpen(true);
                break;
              case 'Current Lifestyle':
                setIsHealthLifestyleEditOpen(true);
                break;
              case 'Medical History':
                setIsMedicalHistoryEditOpen(true);
                break;
              case 'Female Sex Hormones':
                setIsFemaleHormonesEditOpen(true);
                break;
              case 'MRI Scan Safety and Clinical Considerations':
                setIsScanInfoEditOpen(true);
                break;
              case 'Goals':
                setIsGoalsEditOpen(true);
                break;
              default:
                break;
            }
          }}
        />

      </div>
      {/* Edit Personal Information modal */}
      <Modal
        isOpen={isGeneralInfoEditOpen}
        title='Personal Information'
        onClose={() => {
          setIsGeneralInfoEditOpen(false);
          setSaveError(null);
          setPersonalInfoErrors({});
        }}
        width={'600px'}
      >
        <div className='general-info-edit-modal'>
          {saveError && (
            <div className='error-msg' style={{ marginBottom: '15px' }}>
              {saveError}
            </div>
          )}
          <div className='key-value d-flex' style={{ gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <TextInput
                title='First Name *'
                type='text'
                name='first_name'
                id='first_name_input'
                placeholder='Enter first name'
                value={firstName}
                isError={!!personalInfoErrors.first_name}
                errorMessage={personalInfoErrors.first_name}
                onChange={(e) => {
                  const value = e.target.value;
                  setFirstName(value);
                  if (/[^a-zA-Z\s]/.test(value) && value.trim() !== '') {
                    setPersonalInfoErrors((prev) => ({
                      ...prev,
                      first_name: 'First name can only contain letters'
                    }));
                  } else {
                    clearFieldError(setPersonalInfoErrors, 'first_name');
                  }
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextInput
                title='Last Name *'
                type='text'
                name='last_name'
                id='last_name_input'
                placeholder='Enter last name'
                value={lastName}
                isError={!!personalInfoErrors.last_name}
                errorMessage={personalInfoErrors.last_name}
                onChange={(e) => {
                  const value = e.target.value;
                  setLastName(value);
                  if (/[^a-zA-Z\s]/.test(value) && value.trim() !== '') {
                    setPersonalInfoErrors((prev) => ({
                      ...prev,
                      last_name: 'Last name can only contain letters'
                    }));
                  } else {
                    clearFieldError(setPersonalInfoErrors, 'last_name');
                  }
                }}
              />
            </div>
          </div>
          <div className='key-value top-space'>
            <DatePickerComp
              title='Date of Birth *'
              placeholder='Enter date of birth'
              onDateChange={(date) => {
                setSelectedBirthDate(date);
                clearFieldError(setPersonalInfoErrors, 'birthDate');
              }}
              selectedDate={selectedBirthDate}
              showTimeInput={false}
              showYearDropdown={true}
              subMaxDateDays={5840}
            />
            {personalInfoErrors.birthDate && (
              <div className='error-message'>{personalInfoErrors.birthDate}</div>
            )}
          </div>
          <div className='key-value top-space'>
            <Select
              title='Sex Assigned at Birth *'
              name='biological_sex'
              placeholder='Select sex assigned at birth'
              options={genders}
              selectedOption={selectedGender || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedGender(option);
                clearFieldError(setPersonalInfoErrors, 'gender');
              }}
              isError={!!personalInfoErrors.gender}
              errorMessage={personalInfoErrors.gender}
            />
          </div>
          <div className='key-value top-space'>
            <div className='d-flex' style={{ gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  title='Weight *'
                  type='tel'
                  name='weight'
                  id='weight_input'
                  placeholder='Enter weight'
                  value={weightValue}
                  isError={!!personalInfoErrors.weight}
                  errorMessage={personalInfoErrors.weight}
                  onChange={(e) => {
                    const value = e.target.value;
                    setWeightValue(value);
                    if (/[a-zA-Z]/.test(value)) {
                      setPersonalInfoErrors((prev) => ({
                        ...prev,
                        weight: 'Weight must contain only numbers'
                      }));
                    } else {
                      clearFieldError(setPersonalInfoErrors, 'weight');
                    }
                  }}
                />
              </div>
              <div style={{ width: '120px', marginTop: '28px' }}>
                <Select
                  name='weight_unit'
                  placeholder='Unit'
                  options={weightMeasurementTypes}
                  selectedOption={selectedWeightUnit || { key: '', value: '' }}
                  onChange={handleWeightUnitChange}
                  isError={false}
                />
              </div>
            </div>
          </div>
          <div className='key-value top-space'>
            <TextInput
              title='Height (cm) *'
              type='tel'
              name='height'
              id='height_input'
              placeholder='Enter height in cm'
              value={heightValue}
              isError={!!personalInfoErrors.height}
              errorMessage={personalInfoErrors.height}
              onChange={(e) => {
                const value = e.target.value;
                setHeightValue(value);
                if (/[a-zA-Z]/.test(value)) {
                  setPersonalInfoErrors((prev) => ({
                    ...prev,
                    height: 'Height must contain only numbers'
                  }));
                } else {
                  clearFieldError(setPersonalInfoErrors, 'height');
                }
              }}
            />
          </div>
          <div className='key-value top-space'>
            <Select
              title='Race/ethnicity (this can be relevant for your test results) *'
              name='race_ethnicity'
              placeholder='Select race/ethnicity'
              options={races}
              selectedOption={selectedRaceEthnicity || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedRaceEthnicity(option);
                clearFieldError(setPersonalInfoErrors, 'race');
              }}
              isError={!!personalInfoErrors.race}
              errorMessage={personalInfoErrors.race}
            />
          </div>
          <div className='key-value top-space'>
            <TextInput
              title='Email *'
              type='email'
              name='email'
              id='email_input'
              placeholder='Email'
              value={emailDisplay}
              isDisabled
              isError={!!personalInfoErrors.email}
              errorMessage={personalInfoErrors.email}
            />
          </div>
          <div className='key-value top-space'>
            <PhoneInputComp
              title='Mobile phone number *'
              placeholder='Enter phone'
              selectedPhone={phoneDisplay}
              onPhoneChange={() => {}}
              isDisabled
            />
            {personalInfoErrors.phone && (
              <div className='error-message'>{personalInfoErrors.phone}</div>
            )}
          </div>
          <div className='key-value top-space'>
            <label className='key-label'>Address *</label>
            <CustomInput
              value={addressValue}
              onChange={(value: string, postalCode?: string) => {
                const city =
                  (document.getElementsByClassName('city')[0] as HTMLInputElement)?.value || '';
                const country =
                  (document.getElementsByClassName('country')[0] as HTMLInputElement)?.value || '';
                const line1 =
                  (document.getElementsByClassName('line-1')[0] as HTMLInputElement)?.value || '';
                const postcode =
                  postalCode ||
                  (document.getElementsByClassName('postcode')[0] as HTMLInputElement)?.value ||
                  '';
                setAddressValue(value);
                if (line1) setAddressLine1(line1);
                if (city) setCityValue(city);
                if (postcode) setPostcodeValue(postcode);
                if (country) setCountryValue(country);
                clearFieldError(setPersonalInfoErrors, 'full_address');
                clearFieldError(setPersonalInfoErrors, 'address_line_1');
                clearFieldError(setPersonalInfoErrors, 'city');
                clearFieldError(setPersonalInfoErrors, 'postalCode');
                clearFieldError(setPersonalInfoErrors, 'country');
              }}
              id='address-search-medical-card'
            />
            {(personalInfoErrors.full_address || personalInfoErrors.address_line_1) && (
              <div className='error-message'>
                {personalInfoErrors.full_address || personalInfoErrors.address_line_1}
              </div>
            )}
          </div>
          <div className='key-value top-space'>
            <TextInput
              title='City *'
              type='text'
              name='city'
              id='city_input'
              placeholder='Enter city'
              value={cityValue}
              isError={!!personalInfoErrors.city}
              errorMessage={personalInfoErrors.city}
              onChange={(e) => {
                setCityValue(e.target.value);
                clearFieldError(setPersonalInfoErrors, 'city');
              }}
            />
          </div>
          <div className='key-value top-space'>
            <TextInput
              title='Postal code *'
              type='tel'
              name='postcode'
              id='postcode_input'
              placeholder='Enter postal code'
              value={postcodeValue}
              isError={!!personalInfoErrors.postalCode}
              errorMessage={personalInfoErrors.postalCode}
              onChange={(e) => {
                setPostcodeValue(e.target.value);
                clearFieldError(setPersonalInfoErrors, 'postalCode');
              }}
            />
          </div>
          <div className='key-value top-space'>
            <TextInput
              title='Country *'
              type='text'
              name='country'
              id='country_input'
              placeholder='Enter country'
              value={countryValue}
              isError={!!personalInfoErrors.country}
              errorMessage={personalInfoErrors.country}
              onChange={(e) => {
                setCountryValue(e.target.value);
                clearFieldError(setPersonalInfoErrors, 'country');
              }}
            />
          </div>

          <div className='edit-modal-actions d-flex justify-content-end top-space'>
            <Button
              text='Save'
              onClick={async () => {
                const errors = validatePersonalInfo();
                setPersonalInfoErrors(errors);
                if (Object.keys(errors).length > 0) return;

                const line1 =
                  addressLine1 ||
                  (document.getElementsByClassName('line-1')[0] as HTMLInputElement)?.value ||
                  address.address_line_1 ||
                  '';
                const fullAddress =
                  addressValue ||
                  [line1, cityValue, postcodeValue, countryValue]
                    .filter(Boolean)
                    .join(', ') ||
                  medicalCard.basics?.address?.full_address;

                let formattedBirthDate: string | null = null;
                if (selectedBirthDate) {
                  const year = selectedBirthDate.getFullYear();
                  const month = String(selectedBirthDate.getMonth() + 1).padStart(2, '0');
                  const day = String(selectedBirthDate.getDate()).padStart(2, '0');
                  formattedBirthDate = `${year}-${month}-${day}`;
                }

                const updatedBasics = {
                  ...medicalCard.basics,
                  first_name: firstName.trim(),
                  last_name: lastName.trim(),
                  birth_date: formattedBirthDate || medicalCard.basics?.birth_date,
                  biological_sex: selectedGender?.key || medicalCard.basics?.biological_sex,
                  race_ethnicity: selectedRaceEthnicity?.key || medicalCard.basics?.race_ethnicity,
                  weight: weightValue ? Number(weightValue) : medicalCard.basics?.weight,
                  weight_unit: selectedWeightUnit?.key || medicalCard.basics?.weight_unit,
                  height: heightValue ? Number(heightValue) : medicalCard.basics?.height,
                  height_unit: 'cm',
                  email: basicsAny?.email || memberData.email,
                  phone: basicsAny?.phone || memberData.phone,
                  address: {
                    ...medicalCard.basics?.address,
                    full_address: fullAddress,
                    address_line_1: line1,
                    city: cityValue.trim() || address.city,
                    postcode: postcodeValue.trim() || address.postcode,
                    country: countryValue.trim() || address.country
                  }
                };

                const previousGender = medicalCard.basics?.biological_sex;
                const newGender = selectedGender?.key || medicalCard.basics?.biological_sex;
                const genderChangedFromFemaleToMale =
                  previousGender === 'female' && newGender === 'male';

                const updatePayload: any = { basics: updatedBasics };
                if (genderChangedFromFemaleToMale) {
                  updatePayload.health_lifestyle = {
                    ...medicalCard.health_lifestyle,
                    pregnancy: false
                  };
                }

                const success = await handleUpdateMedicalCard(updatePayload);
                if (success) {
                  setIsGeneralInfoEditOpen(false);
                }
              }}
              isLoading={isSaving}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Goals modal */}
      <Modal
        isOpen={isGoalsEditOpen}
        title='Goals'
        onClose={() => {
          setIsGoalsEditOpen(false);
          setSaveError(null);
          setGoalsErrors({});
        }}
        width={'600px'}
      >
        <div className='general-info-edit-modal'>
          {saveError && (
            <div className='error-msg' style={{ marginBottom: '15px' }}>
              {saveError}
            </div>
          )}
          <div className='key-value'>
            <MultiSelect
              title='What brings you to CoreVitals today?'
              name='goals'
              options={goalsOptions}
              selectedOptions={selectedGoals}
              onChange={handleGoalsChange}
              placeholder='Select goals'
              isError={false}
            />
          </div>
          <div className='key-value top-space'>
            <MultiSelect
              title="Do you have any health concerns you'd like to explore further? *"
              name='health_concerns'
              options={healthConcernsOptions}
              selectedOptions={selectedHealthConcerns}
              onChange={handleHealthConcernsChange}
              placeholder='Select health concerns'
              isError={!!goalsErrors.health_concerns}
              errorMessage={goalsErrors.health_concerns}
            />
            {goalsErrors.health_concerns && (
              <div className='error-message'>{goalsErrors.health_concerns}</div>
            )}
          </div>
          {hasConcernDetailsRequired && (
            <div className='key-value top-space'>
              <TextArea
                title='If you selected any of the above, please give details: *'
                name='health_concern_details'
                placeholder='Enter details'
                value={healthConcernDetails}
                rows={3}
                isError={!!goalsErrors.health_concern_details}
                onChange={(e) => {
                  setHealthConcernDetails(e.target.value);
                  clearFieldError(setGoalsErrors, 'health_concern_details');
                }}
              />
              {goalsErrors.health_concern_details && (
                <div className='error-message'>{goalsErrors.health_concern_details}</div>
              )}
            </div>
          )}
          <div className='key-value top-space'>
            <DatePickerComp
              title='When did you last have a blood test?'
              placeholder='Select last blood test date'
              onDateChange={(date) => setSelectedLastBloodTest(date)}
              selectedDate={selectedLastBloodTest}
              showTimeInput={false}
              subMaxDateDays={0}
            />
          </div>
          <div className='key-value top-space'>
            <TextInput
              title="Is there anything specific you'd like our clinical team to know before your assessment?"
              type='text'
              name='additional_info'
              id='additional_info_input'
              placeholder='Enter additional info'
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
            />
          </div>

          <div className='edit-modal-actions d-flex justify-content-end top-space'>
            <Button
              text='Save'
              onClick={async () => {
                const errors = validateGoals();
                setGoalsErrors(errors);
                if (Object.keys(errors).length > 0) return;

                let formattedDate: string | null = null;
                if (selectedLastBloodTest) {
                  const year = selectedLastBloodTest.getFullYear();
                  const month = String(selectedLastBloodTest.getMonth() + 1).padStart(2, '0');
                  const day = String(selectedLastBloodTest.getDate()).padStart(2, '0');
                  formattedDate = `${year}-${month}-${day}`;
                }
                
                const updatedGoals = {
                  ...medicalCard.goals,
                  goals: selectedGoals.map((goal) => goal.key),
                  health_concerns: selectedHealthConcerns.map((concern) => concern.key),
                  health_concern_details: hasConcernDetailsRequired
                    ? healthConcernDetails
                    : '',
                  last_blood_test: formattedDate || medicalCard.goals?.last_blood_test || null,
                  additional_info: additionalInfo
                };
                
                const success = await handleUpdateMedicalCard({ goals: updatedGoals });
                if (success) {
                  setIsGoalsEditOpen(false);
                }
              }}
              isLoading={isSaving}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Current Lifestyle modal */}
      <Modal
        isOpen={isHealthLifestyleEditOpen}
        title='Current Lifestyle'
        onClose={() => {
          setIsHealthLifestyleEditOpen(false);
          setSaveError(null);
          setLifestyleErrors({});
        }}
        width={'600px'}
      >
        <div className='general-info-edit-modal'>
          {saveError && (
            <div className='error-msg' style={{ marginBottom: '15px' }}>
              {saveError}
            </div>
          )}
          <div className='key-value'>
            <Select
              title='How would you describe your overall health? *'
              name='overall_health'
              placeholder='Select overall health'
              options={overallHealthOptions}
              selectedOption={selectedOverallHealth || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedOverallHealth(option);
                clearFieldError(setLifestyleErrors, 'overall_health');
              }}
              isError={!!lifestyleErrors.overall_health}
              errorMessage={lifestyleErrors.overall_health}
            />
          </div>
          <div className='key-value top-space'>
            <Select
              title='How would you describe your activity level? *'
              name='activity_level'
              placeholder='Select activity level'
              options={activityLevelOptions}
              selectedOption={selectedActivityLevel || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedActivityLevel(option);
                clearFieldError(setLifestyleErrors, 'activity_level');
              }}
              isError={!!lifestyleErrors.activity_level}
              errorMessage={lifestyleErrors.activity_level}
            />
          </div>
          <div className='key-value top-space'>
            <Select
              title='How would you describe your exercise routine? *'
              name='exercise_routine'
              placeholder='Select exercise routine'
              options={exerciseRoutineOptions}
              selectedOption={selectedExerciseRoutine || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedExerciseRoutine(option);
                clearFieldError(setLifestyleErrors, 'exercise_routine');
                if (option.key !== 'Other') {
                  setOtherExerciseRoutine('');
                  clearFieldError(setLifestyleErrors, 'other_exercise_routine');
                }
              }}
              isError={!!lifestyleErrors.exercise_routine}
              errorMessage={lifestyleErrors.exercise_routine}
            />
          </div>
          {selectedExerciseRoutine?.key === 'Other' && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify your exercise routine *'
                type='text'
                name='other_exercise_routine'
                id='other_exercise_routine_input'
                placeholder='Enter other exercise routine'
                value={otherExerciseRoutine}
                isError={!!lifestyleErrors.other_exercise_routine}
                errorMessage={lifestyleErrors.other_exercise_routine}
                onChange={(e) => {
                  setOtherExerciseRoutine(e.target.value);
                  clearFieldError(setLifestyleErrors, 'other_exercise_routine');
                }}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <Select
              title='Do you smoke or use nicotine products? *'
              name='smoking_status'
              placeholder='Select smoking status'
              options={smokingStatusOptions}
              selectedOption={selectedSmokingStatus || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedSmokingStatus(option);
                clearFieldError(setLifestyleErrors, 'smoking_status');
              }}
              isError={!!lifestyleErrors.smoking_status}
              errorMessage={lifestyleErrors.smoking_status}
            />
          </div>
          <div className='key-value top-space'>
            <Select
              title='How often do you drink alcohol? *'
              name='alcohol_frequency'
              placeholder='Select alcohol frequency'
              options={alcoholFrequencyOptions}
              selectedOption={selectedAlcoholFrequency || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedAlcoholFrequency(option);
                clearFieldError(setLifestyleErrors, 'alcohol_frequency');
              }}
              isError={!!lifestyleErrors.alcohol_frequency}
              errorMessage={lifestyleErrors.alcohol_frequency}
            />
          </div>
          <div className='key-value top-space'>
            <Select
              title='What best describes your working style? *'
              name='working_style'
              placeholder='Select working style'
              options={workingStyleOptions}
              selectedOption={selectedWorkingStyle || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedWorkingStyle(option);
                clearFieldError(setLifestyleErrors, 'working_style');
                if (option.key !== 'Other') {
                  setOtherWorkingStyle('');
                  clearFieldError(setLifestyleErrors, 'other_working_style');
                }
              }}
              isError={!!lifestyleErrors.working_style}
              errorMessage={lifestyleErrors.working_style}
            />
          </div>
          {selectedWorkingStyle?.key === 'Other' && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify your working style *'
                type='text'
                name='other_working_style'
                id='other_working_style_input'
                placeholder='Enter other working style'
                value={otherWorkingStyle}
                isError={!!lifestyleErrors.other_working_style}
                errorMessage={lifestyleErrors.other_working_style}
                onChange={(e) => {
                  setOtherWorkingStyle(e.target.value);
                  clearFieldError(setLifestyleErrors, 'other_working_style');
                }}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <MultiSelect
              title='Do you follow a particular diet? *'
              name='diet_types'
              options={dietTypeOptions}
              selectedOptions={selectedDietTypes}
              onChange={handleDietTypesChange}
              placeholder='Select diet types'
              isError={!!lifestyleErrors.diet_types}
            />
            {lifestyleErrors.diet_types && (
              <div className='error-message'>{lifestyleErrors.diet_types}</div>
            )}
          </div>
          {selectedDietTypes.some((diet) => diet.key === 'Other') && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify your diet type *'
                type='text'
                name='other_diet_type'
                id='other_diet_type_input'
                placeholder='Enter other diet type'
                value={otherDietType}
                isError={!!lifestyleErrors.other_diet_type}
                errorMessage={lifestyleErrors.other_diet_type}
                onChange={(e) => {
                  setOtherDietType(e.target.value);
                  clearFieldError(setLifestyleErrors, 'other_diet_type');
                }}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <Select
              title='How would you describe your typical eating pattern? *'
              name='eating_pattern'
              placeholder='Select eating pattern'
              options={eatingPatternOptions}
              selectedOption={selectedEatingPattern || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedEatingPattern(option);
                clearFieldError(setLifestyleErrors, 'eating_pattern');
                if (option.key !== 'Other') {
                  setOtherEatingPattern('');
                  clearFieldError(setLifestyleErrors, 'other_eating_pattern');
                }
              }}
              isError={!!lifestyleErrors.eating_pattern}
              errorMessage={lifestyleErrors.eating_pattern}
            />
          </div>
          {selectedEatingPattern?.key === 'Other' && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify your eating pattern *'
                type='text'
                name='other_eating_pattern'
                id='other_eating_pattern_input'
                placeholder='Enter other eating pattern'
                value={otherEatingPattern}
                isError={!!lifestyleErrors.other_eating_pattern}
                errorMessage={lifestyleErrors.other_eating_pattern}
                onChange={(e) => {
                  setOtherEatingPattern(e.target.value);
                  clearFieldError(setLifestyleErrors, 'other_eating_pattern');
                }}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <Select
              title='How do you usually hydrate? *'
              name='hydration'
              placeholder='Select hydration method'
              options={hydrationOptions}
              selectedOption={selectedHydration || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedHydration(option);
                clearFieldError(setLifestyleErrors, 'hydration');
                if (option.key !== 'Other') {
                  setOtherHydration('');
                  clearFieldError(setLifestyleErrors, 'other_hydration');
                }
              }}
              isError={!!lifestyleErrors.hydration}
              errorMessage={lifestyleErrors.hydration}
            />
          </div>
          {selectedHydration?.key === 'Other' && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify your hydration method *'
                type='text'
                name='other_hydration'
                id='other_hydration_input'
                placeholder='Enter other hydration'
                value={otherHydration}
                isError={!!lifestyleErrors.other_hydration}
                errorMessage={lifestyleErrors.other_hydration}
                onChange={(e) => {
                  setOtherHydration(e.target.value);
                  clearFieldError(setLifestyleErrors, 'other_hydration');
                }}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <MultiSelect
              title='Do you practise any calming or restorative activities? *'
              name='calming_activities'
              options={calmingActivityOptions}
              selectedOptions={selectedCalmingActivities}
              onChange={handleCalmingActivitiesChange}
              placeholder='Select calming activities'
              isError={!!lifestyleErrors.calming_activities}
            />
            {lifestyleErrors.calming_activities && (
              <div className='error-message'>{lifestyleErrors.calming_activities}</div>
            )}
          </div>
          {selectedCalmingActivities.some((act) => act.key === 'Other') && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify your calming activity *'
                type='text'
                name='other_calming_activity'
                id='other_calming_activity_input'
                placeholder='Enter other calming activity'
                value={otherCalmingActivity}
                isError={!!lifestyleErrors.other_calming_activity}
                errorMessage={lifestyleErrors.other_calming_activity}
                onChange={(e) => {
                  setOtherCalmingActivity(e.target.value);
                  clearFieldError(setLifestyleErrors, 'other_calming_activity');
                }}
              />
            </div>
          )}

          <div className='edit-modal-actions d-flex justify-content-end top-space'>
            <Button
              text='Save'
              onClick={async () => {
                const errors = validateLifestyle();
                setLifestyleErrors(errors);
                if (Object.keys(errors).length > 0) return;

                const updatedHealthLifestyle = {
                  ...medicalCard.health_lifestyle,
                  overall_health: selectedOverallHealth?.key,
                  activity_level: selectedActivityLevel?.key,
                  alcohol_frequency: selectedAlcoholFrequency?.key,
                  smoking_status: selectedSmokingStatus?.key
                };

                const updatedNutritionistQuestions = {
                  ...medicalCard.nutritionist_questions,
                  exercise_routine: selectedExerciseRoutine?.key,
                  other_exercise_routine:
                    selectedExerciseRoutine?.key === 'Other' ? otherExerciseRoutine : null,
                  working_style: selectedWorkingStyle?.key,
                  other_working_style:
                    selectedWorkingStyle?.key === 'Other' ? otherWorkingStyle : null,
                  diet_types: selectedDietTypes.map((diet) => diet.key),
                  other_diet_type: selectedDietTypes.some((d) => d.key === 'Other')
                    ? otherDietType
                    : null,
                  eating_pattern: selectedEatingPattern?.key,
                  other_eating_pattern:
                    selectedEatingPattern?.key === 'Other' ? otherEatingPattern : null,
                  hydration: selectedHydration?.key,
                  other_hydration:
                    selectedHydration?.key === 'Other' ? otherHydration : null,
                  calming_activities: selectedCalmingActivities.map((act) => act.key),
                  other_calming_activity: selectedCalmingActivities.some(
                    (a) => a.key === 'Other'
                  )
                    ? otherCalmingActivity
                    : null
                };

                const success = await handleUpdateMedicalCard({
                  health_lifestyle: updatedHealthLifestyle,
                  nutritionist_questions: updatedNutritionistQuestions
                });
                if (success) {
                  setIsHealthLifestyleEditOpen(false);
                }
              }}
              isLoading={isSaving}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Medical History modal */}
      <Modal
        isOpen={isMedicalHistoryEditOpen}
        title='Medical History'
        onClose={() => {
          setIsMedicalHistoryEditOpen(false);
          setSaveError(null);
          setMedicalHistoryErrors({});
        }}
        width={'600px'}
      >
        <div className='general-info-edit-modal'>
          {saveError && (
            <div className='error-msg' style={{ marginBottom: '15px' }}>
              {saveError}
            </div>
          )}
          <MedicalCardYesNoDescribe
            title='Do you have any past or ongoing medical conditions we should know about?'
            answer={hasMedicalConditions}
            detail={medicalConditionsDetail}
            detailPlaceholder='Describe your medical conditions'
            answerError={medicalHistoryErrors.has_medical_conditions}
            detailError={medicalHistoryErrors.medical_conditions}
            onAnswerChange={(answer) => {
              setHasMedicalConditions(answer);
              clearFieldError(setMedicalHistoryErrors, 'has_medical_conditions');
              if (answer === 'no') {
                setMedicalConditionsDetail('');
                clearFieldError(setMedicalHistoryErrors, 'medical_conditions');
              }
            }}
            onDetailChange={(value) => {
              setMedicalConditionsDetail(value);
              clearFieldError(setMedicalHistoryErrors, 'medical_conditions');
            }}
          />
          <MedicalCardYesNoDescribe
            title='Have you had any previous surgeries or medical procedures?'
            answer={hasSurgeries}
            detail={surgeriesDetail}
            detailPlaceholder='Describe your surgeries or medical procedures'
            answerError={medicalHistoryErrors.has_surgeries}
            detailError={medicalHistoryErrors.previous_surgeries}
            onAnswerChange={(answer) => {
              setHasSurgeries(answer);
              clearFieldError(setMedicalHistoryErrors, 'has_surgeries');
              if (answer === 'no') {
                setSurgeriesDetail('');
                clearFieldError(setMedicalHistoryErrors, 'previous_surgeries');
              }
            }}
            onDetailChange={(value) => {
              setSurgeriesDetail(value);
              clearFieldError(setMedicalHistoryErrors, 'previous_surgeries');
            }}
          />
          <MedicalCardYesNoDescribe
            title='Are you currently taking any medications or supplements?'
            answer={hasMedications}
            detail={medicationsDetail}
            detailPlaceholder='Enter regular medications or supplements'
            answerError={medicalHistoryErrors.has_medications_supplements}
            detailError={medicalHistoryErrors.supplements}
            onAnswerChange={(answer) => {
              setHasMedications(answer);
              clearFieldError(setMedicalHistoryErrors, 'has_medications_supplements');
              if (answer === 'no') {
                setMedicationsDetail('');
                clearFieldError(setMedicalHistoryErrors, 'supplements');
              }
            }}
            onDetailChange={(value) => {
              setMedicationsDetail(value);
              clearFieldError(setMedicalHistoryErrors, 'supplements');
            }}
          />
          <MedicalCardYesNoDescribe
            title='Do you have any known allergies? If yes, please list the allergen and describe the reaction (e.g. penicillin, anaphylaxis; latex, skin reaction; contrast dye, nausea).'
            answer={hasAllergies}
            detail={allergiesDetail}
            detailPlaceholder='List the allergen and describe the reaction'
            answerError={medicalHistoryErrors.has_allergies}
            detailError={medicalHistoryErrors.allergies}
            onAnswerChange={(answer) => {
              setHasAllergies(answer);
              clearFieldError(setMedicalHistoryErrors, 'has_allergies');
              if (answer === 'no') {
                setAllergiesDetail('');
                clearFieldError(setMedicalHistoryErrors, 'allergies');
              }
            }}
            onDetailChange={(value) => {
              setAllergiesDetail(value);
              clearFieldError(setMedicalHistoryErrors, 'allergies');
            }}
          />

          <div className='edit-modal-actions d-flex justify-content-end top-space'>
            <Button
              text='Save'
              onClick={async () => {
                const errors = validateMedicalHistory();
                setMedicalHistoryErrors(errors);
                if (Object.keys(errors).length > 0) return;

                const storeYesNo = (answer: YesNoAnswer | undefined, detail: string) =>
                  answer === 'no' ? '' : answer === 'yes' ? detail : '';

                const updatedHealthLifestyle = {
                  ...medicalCard.health_lifestyle,
                  medical_conditions: storeYesNo(
                    hasMedicalConditions,
                    medicalConditionsDetail
                  ),
                  previous_surgeries: storeYesNo(hasSurgeries, surgeriesDetail),
                  supplements: storeYesNo(hasMedications, medicationsDetail),
                  allergies: storeYesNo(hasAllergies, allergiesDetail)
                };

                const success = await handleUpdateMedicalCard({
                  health_lifestyle: updatedHealthLifestyle
                });
                if (success) {
                  setIsMedicalHistoryEditOpen(false);
                }
              }}
              isLoading={isSaving}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Female Sex Hormones modal */}
      <Modal
        isOpen={isFemaleHormonesEditOpen}
        title='Female Sex Hormones'
        onClose={() => {
          setIsFemaleHormonesEditOpen(false);
          setSaveError(null);
        }}
        width={'600px'}
      >
        <div className='general-info-edit-modal'>
          {saveError && (
            <div className='error-msg' style={{ marginBottom: '15px' }}>
              {saveError}
            </div>
          )}
          <div className='key-value'>
            <DatePickerComp
              title='What was the date of your last menstrual period?'
              placeholder='Select last menstrual period date'
              onDateChange={(date) => setSelectedLastPeriodDate(date)}
              selectedDate={selectedLastPeriodDate}
              showTimeInput={false}
              subMaxDateDays={0}
            />
          </div>
          <div className='key-value top-space'>
            <Select
              title='Do you currently take hormonal contraception or HRT? If so, please provide the name and how long you have taken this for.'
              name='hormonal_contraception_hrt'
              placeholder='Select hormonal contraception or HRT'
              options={hormonalContraceptionOptions}
              selectedOption={selectedHormonalContraception || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedHormonalContraception(option);
                if (option.key !== 'Yes - Other') {
                  setHormonalContraceptionOther('');
                }
              }}
              isError={false}
            />
          </div>
          {selectedHormonalContraception?.key === 'Yes - Other' && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify hormonal contraception or HRT'
                type='text'
                name='hormonal_contraception_hrt_other'
                id='hormonal_contraception_hrt_other_input'
                placeholder='Enter details'
                value={hormonalContraceptionOther}
                onChange={(e) => setHormonalContraceptionOther(e.target.value)}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <Select
              title='Do you know your typical cycle length?'
              name='typical_cycle_length'
              placeholder='Select cycle length'
              options={cycleLengthOptions}
              selectedOption={selectedCycleLength || { key: '', value: '' }}
              onChange={(option) => setSelectedCycleLength(option)}
              isError={false}
            />
          </div>
          <div className='key-value top-space'>
            <Select
              title='Primary reason for testing (e.g., PMS, fertility concerns, cycle mapping, fatigue)?'
              name='primary_testing_reason'
              placeholder='Select primary reason for testing'
              options={primaryTestingReasonOptions}
              selectedOption={selectedPrimaryTestingReason || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedPrimaryTestingReason(option);
                if (option.key !== 'Other') {
                  setPrimaryTestingReasonOther('');
                }
              }}
              isError={false}
            />
          </div>
          {selectedPrimaryTestingReason?.key === 'Other' && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify primary reason for testing'
                type='text'
                name='primary_testing_reason_other'
                id='primary_testing_reason_other_input'
                placeholder='Enter primary reason for testing'
                value={primaryTestingReasonOther}
                onChange={(e) => setPrimaryTestingReasonOther(e.target.value)}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <Select
              title='Menstrual flow characteristics (e.g., heavy, light, painful, presence of spotting)?'
              name='menstrual_flow_characteristics'
              placeholder='Select menstrual flow characteristics'
              options={menstrualFlowOptions}
              selectedOption={selectedMenstrualFlow || { key: '', value: '' }}
              onChange={(option) => {
                setSelectedMenstrualFlow(option);
                if (option.key !== 'Other') {
                  setMenstrualFlowOther('');
                }
              }}
              isError={false}
            />
          </div>
          {selectedMenstrualFlow?.key === 'Other' && (
            <div className='key-value top-space'>
              <TextInput
                title='Please specify menstrual flow characteristics'
                type='text'
                name='menstrual_flow_characteristics_other'
                id='menstrual_flow_characteristics_other_input'
                placeholder='Enter menstrual flow characteristics'
                value={menstrualFlowOther}
                onChange={(e) => setMenstrualFlowOther(e.target.value)}
              />
            </div>
          )}
          <div className='key-value top-space'>
            <TextInput
              title='If postmenopausal, age at last menstrual period or whether surgical menopause occurred?'
              type='text'
              name='menopause_details'
              id='menopause_details_input'
              placeholder='Enter age at last menstrual period or whether surgical menopause occurred'
              value={menopauseDetails}
              onChange={(e) => setMenopauseDetails(e.target.value)}
            />
          </div>
          <div className='key-value top-space'>
            <TextInput
              title="Is there anything specific you'd like our clinical team to know before your assessment?"
              type='text'
              name='additional_notes_for_clinical_team'
              id='additional_notes_for_clinical_team_input'
              placeholder='Enter additional notes for clinical team'
              value={additionalNotesForClinicalTeam}
              onChange={(e) => setAdditionalNotesForClinicalTeam(e.target.value)}
            />
          </div>
          <div className='edit-modal-actions d-flex justify-content-end top-space'>
            <Button
              text='Save'
              onClick={async () => {
                let formattedLastPeriodDate: string | null = null;
                if (selectedLastPeriodDate) {
                  const year = selectedLastPeriodDate.getFullYear();
                  const month = String(selectedLastPeriodDate.getMonth() + 1).padStart(2, '0');
                  const day = String(selectedLastPeriodDate.getDate()).padStart(2, '0');
                  formattedLastPeriodDate = `${year}-${month}-${day}`;
                }
                
                let hormonalContraceptionValue =
                  selectedHormonalContraception?.key ||
                  medicalCard.female_sex_hormones?.hormonal_contraception_hrt;
                if (
                  selectedHormonalContraception?.key === 'Yes - Other' &&
                  hormonalContraceptionOther.trim()
                ) {
                  hormonalContraceptionValue = hormonalContraceptionOther.trim();
                }
                
                let primaryTestingReasonValue =
                  selectedPrimaryTestingReason?.key ||
                  medicalCard.female_sex_hormones?.primary_testing_reason;
                if (
                  selectedPrimaryTestingReason?.key === 'Other' &&
                  primaryTestingReasonOther.trim()
                ) {
                  primaryTestingReasonValue = primaryTestingReasonOther.trim();
                }
                
                let menstrualFlowValue =
                  selectedMenstrualFlow?.key ||
                  medicalCard.female_sex_hormones?.menstrual_flow_characteristics;
                if (selectedMenstrualFlow?.key === 'Other' && menstrualFlowOther.trim()) {
                  menstrualFlowValue = menstrualFlowOther.trim();
                }
                
                const updatedFemaleHormones = {
                  ...medicalCard.female_sex_hormones,
                  last_period_date:
                    formattedLastPeriodDate ||
                    medicalCard.female_sex_hormones?.last_period_date ||
                    null,
                  hormonal_contraception_hrt: hormonalContraceptionValue || null,
                  typical_cycle_length:
                    selectedCycleLength?.key ||
                    medicalCard.female_sex_hormones?.typical_cycle_length ||
                    null,
                  primary_testing_reason: primaryTestingReasonValue || null,
                  menstrual_flow_characteristics: menstrualFlowValue || null,
                  menopause_details: menopauseDetails || null,
                  additional_notes_for_clinical_team: additionalNotesForClinicalTeam || null
                };
                
                const success = await handleUpdateMedicalCard({
                  female_sex_hormones: updatedFemaleHormones
                });
                if (success) {
                  setIsFemaleHormonesEditOpen(false);
                }
              }}
              isLoading={isSaving}
            />
          </div>
        </div>
      </Modal>

      {/* Edit MRI Scan Safety modal */}
      <Modal
        isOpen={isScanInfoEditOpen}
        title='MRI Scan Safety and Clinical Considerations'
        onClose={() => {
          setIsScanInfoEditOpen(false);
          setSaveError(null);
          setScanErrors({});
        }}
        width={'600px'}
      >
        <div className='general-info-edit-modal'>
          {saveError && (
            <div className='error-msg' style={{ marginBottom: '15px' }}>
              {saveError}
            </div>
          )}
          <div className='key-value'>
            <TextArea
              title='What is the primary reason for booking a full body MRI scan with us? *'
              name='reason_for_scan'
              placeholder='Enter reason for scan'
              value={reasonForScan}
              rows={3}
              isError={!!scanErrors.reason_for_scan}
              onChange={(e) => {
                setReasonForScan(e.target.value);
                clearFieldError(setScanErrors, 'reason_for_scan');
              }}
            />
            {scanErrors.reason_for_scan && (
              <div className='error-message'>{scanErrors.reason_for_scan}</div>
            )}
          </div>
          <div className='key-value top-space'>
            <TextArea
              title='Are you currently experiencing any symptoms such as pain, fatigue, or unexplained changes? *'
              name='current_symptoms'
              placeholder='Enter current symptoms'
              value={currentSymptoms}
              rows={3}
              isError={!!scanErrors.current_symptoms}
              onChange={(e) => {
                setCurrentSymptoms(e.target.value);
                clearFieldError(setScanErrors, 'current_symptoms');
              }}
            />
            {scanErrors.current_symptoms && (
              <div className='error-message'>{scanErrors.current_symptoms}</div>
            )}
          </div>
          <div className='key-value top-space'>
            <MedicalCardYesNoDescribe
              title='Have you ever been diagnosed with cancer?'
              answer={cancerAnswer}
              detail={cancerHistoryDetails}
              detailPlaceholder='Include the type, date, and any treatment received'
              answerError={scanErrors.has_cancer_history}
              detailError={scanErrors.cancer_history_details}
              onAnswerChange={(answer) => {
                setCancerAnswer(answer);
                clearFieldError(setScanErrors, 'has_cancer_history');
                if (answer === 'no') {
                  setCancerHistoryDetails('');
                  clearFieldError(setScanErrors, 'cancer_history_details');
                }
              }}
              onDetailChange={(value) => {
                setCancerHistoryDetails(value);
                clearFieldError(setScanErrors, 'cancer_history_details');
              }}
            />
          </div>
          <div className='key-value top-space'>
            <TextArea
              title='Have you had previous medical imaging such as an MRI, CT, or X-ray? If yes, please describe where and approximately when.'
              name='any_previous_imaging'
              placeholder='Describe previous imaging (optional)'
              value={previousImaging}
              rows={3}
              isError={false}
              onChange={(e) => setPreviousImaging(e.target.value)}
            />
          </div>
          <div className='key-value top-space'>
            <TextArea
              title='Is there a family history of cancer, heart disease, or other serious conditions relevant to your reason for booking? If yes, please describe.'
              name='scan_family_history_details'
              placeholder='Describe relevant family history (optional)'
              value={scanFamilyHistory}
              rows={3}
              isError={false}
              onChange={(e) => setScanFamilyHistory(e.target.value)}
            />
          </div>
          <div className='key-value top-space'>
            <MultiSelect
              title='Medical Implants and Metallic Objects - please select all that apply: *'
              name='medical_implants'
              options={medicalImplantOptions}
              selectedOptions={selectedMedicalImplants}
              onChange={(option) =>
                handleExclusiveMultiSelect(
                  option,
                  selectedMedicalImplants,
                  setSelectedMedicalImplants,
                  setImplantDetails,
                  'medical_implants',
                  'medical_implants_details'
                )
              }
              placeholder='Select medical implants'
              isError={!!scanErrors.medical_implants}
            />
            {scanErrors.medical_implants && (
              <div className='error-message'>{scanErrors.medical_implants}</div>
            )}
          </div>
          {hasImplantSelectionRequiringDetails && (
            <div className='key-value top-space'>
              <TextArea
                title='If you selected any of the above, please give details — type, location in your body, and when it was inserted. *'
                name='metallic_implants_details'
                placeholder='Enter implant details'
                value={implantDetails}
                rows={3}
                isError={!!scanErrors.medical_implants_details}
                onChange={(e) => {
                  setImplantDetails(e.target.value);
                  clearFieldError(setScanErrors, 'medical_implants_details');
                }}
              />
              {scanErrors.medical_implants_details && (
                <div className='error-message'>{scanErrors.medical_implants_details}</div>
              )}
            </div>
          )}
          <div className='key-value top-space'>
            <MultiSelect
              title='Practical and Clinical Considerations - please select all that apply: *'
              name='practical_considerations'
              options={practicalConsiderationOptions}
              selectedOptions={selectedPracticalConsiderations}
              onChange={(option) =>
                handleExclusiveMultiSelect(
                  option,
                  selectedPracticalConsiderations,
                  setSelectedPracticalConsiderations,
                  setPracticalDetails,
                  'practical_considerations',
                  'practical_considerations_details'
                )
              }
              placeholder='Select practical considerations'
              isError={!!scanErrors.practical_considerations}
            />
            {scanErrors.practical_considerations && (
              <div className='error-message'>{scanErrors.practical_considerations}</div>
            )}
          </div>
          {hasPracticalSelectionRequiringDetails && (
            <div className='key-value top-space'>
              <TextArea
                title='If you selected any of the above, please give details: *'
                name='practical_considerations_details'
                placeholder='Enter practical consideration details'
                value={practicalDetails}
                rows={3}
                isError={!!scanErrors.practical_considerations_details}
                onChange={(e) => {
                  setPracticalDetails(e.target.value);
                  clearFieldError(setScanErrors, 'practical_considerations_details');
                }}
              />
              {scanErrors.practical_considerations_details && (
                <div className='error-message'>
                  {scanErrors.practical_considerations_details}
                </div>
              )}
            </div>
          )}
          <div className='edit-modal-actions d-flex justify-content-end top-space'>
            <Button
              text='Save'
              onClick={async () => {
                const errors = validateScanInfo();
                setScanErrors(errors);
                if (Object.keys(errors).length > 0) return;

                const existingScanInfo = (medicalCard?.scan_info || {}) as Record<string, any>;

                const updatedScanInfo = {
                  ...existingScanInfo,
                  reason_for_scan: reasonForScan || null,
                  current_symptoms: currentSymptoms || null,
                  cancer_history:
                    cancerAnswer === 'yes'
                      ? 'Yes'
                      : cancerAnswer === 'no'
                        ? 'No'
                        : existingScanInfo.cancer_history || null,
                  cancer_history_details:
                    cancerAnswer === 'yes'
                      ? cancerHistoryDetails || null
                      : cancerAnswer === 'no'
                        ? null
                        : existingScanInfo.cancer_history_details || null,
                  any_previous_imaging: previousImaging || null,
                  scan_family_history_details: scanFamilyHistory || null,
                  medical_implants: selectedMedicalImplants.map((item) => item.key),
                  metallic_implants_details: hasImplantSelectionRequiringDetails
                    ? implantDetails || null
                    : null,
                  practical_considerations: selectedPracticalConsiderations.map(
                    (item) => item.key
                  ),
                  practical_considerations_details: hasPracticalSelectionRequiringDetails
                    ? practicalDetails || null
                    : null
                };

                const success = await handleUpdateMedicalCard({ scan_info: updatedScanInfo });
                if (success) {
                  setIsScanInfoEditOpen(false);
                }
              }}
              isLoading={isSaving}
            />
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default MedicalCardModal;
